import { useSearchParams } from 'next/navigation';
import useSWR from 'swr';

import { usePrivate } from '@/components/private';
import { enqueueSnackbar } from '@/providers/SnackbarProvider';

import { useGetOrganisationsByUserIdMessages } from './messages';
import { SNACKBAR_VARIANT } from '@/constants/notifications';

export interface useGetOrganisationsByUserIdOptions {
  shouldFetch?: boolean;
  organisationId?: number | null;
}

export function useGetOrganisationsByUserId(
  userId: number | null | undefined,
  options?: useGetOrganisationsByUserIdOptions,
) {
  const mergedOptions: useGetOrganisationsByUserIdOptions = {
    shouldFetch: true,
    ...options,
  };

  const { roleUserOrgAssociationApi } = usePrivate();
  const searchParams = useSearchParams();

  const organisationIdFromUrl = searchParams.get('organisationId');
  const urlOrgId = organisationIdFromUrl
    ? parseInt(organisationIdFromUrl, 10)
    : null;
  const validUrlOrgId = urlOrgId && !isNaN(urlOrgId) ? urlOrgId : null;

  const organisationId =
    mergedOptions.organisationId !== undefined
      ? mergedOptions.organisationId
      : validUrlOrgId;

  // Create a stable cache key that includes organisationId
  const cacheKey =
    roleUserOrgAssociationApi && mergedOptions.shouldFetch && userId
      ? `/api/RoleUserOrgAssociation/GetByUserOrgId/${userId}${
          organisationId ? `?organisationId=${organisationId}` : ''
        }`
      : null;

  const results = useSWR(
    cacheKey,
    () =>
      roleUserOrgAssociationApi?.apiRoleUserOrgAssociationGetByUserOrgIdGet(
        userId!,
        organisationId ?? undefined,
      ),
    {
      onError: (error) => {
        enqueueSnackbar(
          useGetOrganisationsByUserIdMessages[
            'useGetOrganisationsByUserId.fetch.unexpectedError'
          ],
          {
            variant: SNACKBAR_VARIANT.ERROR,
          },
        );
        console.error(
          '[BREEDLOGIC] <useGetOrganisationsByUserId> error fetching memberships.',
          error,
        );
      },
    },
  );

  const isLoadingOrValidating =
    results.isLoading || results.isValidating || !roleUserOrgAssociationApi;

  return { ...results, isLoadingOrValidating };
}
